import { observer } from 'mobx-react'
import React, { FC, useEffect, useState, useRef } from 'react'
import { confirmDelete } from '../utils/ConfirmDelete'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import { t } from 'ttag'
import { sortBy } from 'underscore'
import _debug from 'debug'
import { SquareSquare, Square, RectangleHorizontal } from 'lucide-react'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { DBObject, Passage, PassageDocument, move } from '../../models3/ProjectModels'
import { Root } from '../../models3/Root'
import { VideoCache } from '../../models3/VideoCache'
import { AddButton, LockButton, PencilButton, PinButton, TrashButton, UnlockButton, ZoomInDocumentButton, ZoomOutDocumentButton } from '../utils/Buttons'
import { sendBeacon, displayError } from '../utils/Errors'
import { fmt } from '../utils/Fmt'
import { EditableRichText } from '../utils/RichTextEditor'
import './Passage.css'
import { doOnceWhenBackOnline } from '../utils/ServiceStatus'
import { stringifyCreationHistoryForBeacon } from '../utils/PassageDocument'
import { useZoom } from '../utils/useZoom'
import { convertFromCanonicalTimestamp } from '../../models3/utils/dateServices'
import { useOnClickOutside } from '../utils/Hooks'

const log = _debug('sltt:PassageDocuments')


/** user can change scope if admin or document creator */
function canUserChangeScope(rt: Root, dbObject: DBObject) {
    const { iAmAdmin, iAmInterpreter, username } = rt
    return iAmAdmin || (iAmInterpreter /* can create pasDoc */ && dbObject.iAmCreator(username))
}

type PassageDocumentsProps = {
    rt: Root,
    passage: Passage,
}

const PassageDocuments: FC<PassageDocumentsProps> = observer(({ rt, passage }) => {
    const [newDocument, setNewDocument] = useState<PassageDocument|null>(null)
    const flags = useFlags()

    const getAllDocumentsInScope = () => {
        const docOwners = new Map<string, Passage>()
        let allProjectDocs: PassageDocument[] = []
        let allPortionDocs: PassageDocument[] = []
        let allPassageDocs: PassageDocument[] = []
        const portionId = passage._id.split('/')[0]
        const passageId = passage._id.split('/')[1]

        rt.project._portions.forEach(_portion => {
            _portion._passages.forEach(_passage => {
                _passage.documents.forEach(pasDoc => {
                    docOwners.set(pasDoc._id, _passage)
                    const scopeId = pasDoc.scope || _passage._id
                    if (scopeId === 'project') {
                        allProjectDocs.push(pasDoc)
                    } else if (scopeId === portionId) {
                        allPortionDocs.push(pasDoc)
                    } else if (scopeId === `${portionId}/${passageId}`) {
                        allPassageDocs.push(pasDoc)
                    }
                })
            })
        })

        const sortedAllProjectDocs = sortBy(allProjectDocs, 'rank')
        const sortedAllPortionDocs = sortBy(allPortionDocs, 'rank')
        const sortedAllPassageDocs = sortBy(allPassageDocs, 'rank')
        const sortedAllDocs = [...sortedAllProjectDocs, ...sortedAllPortionDocs, ...sortedAllPassageDocs]

        return { docOwners, sortedAllDocs, sortedAllProjectDocs, sortedAllPortionDocs, sortedAllPassageDocs }
    }
    const { sortedAllDocs, sortedAllProjectDocs, sortedAllPortionDocs, sortedAllPassageDocs } = getAllDocumentsInScope()
    
    // Log all documents in order
    log('All documents in order:', fmt(sortedAllDocs.map(doc => `${doc.title || 'Untitled'} (${doc.scope}) [rank: ${doc.rank || 'none'}]`)))
    
    if (newDocument && !sortedAllDocs.some(d => d._id === newDocument._id)){
        // avoid duplicates in the sortedAllDocs array
        // e.g. when a new document is saved/confirmed and thus put into the passage.documents
        sortedAllDocs.push(newDocument)
    }

    const [currentPasDoc, setCurrentPasDoc] = useState<string>(sortedAllDocs[0]?._id)
    const index = sortedAllDocs.findIndex(doc => doc._id === currentPasDoc)

    function addDocument() {
        if (rt.editingPassageDocument) return // should not happen

        let document = passage.createPassageDocument()
        document.text = ''
        let rank = 100
        if (sortedAllPassageDocs.length > 0) {
            rank = sortedAllPassageDocs.slice(-1)[0].rankAsNumber + 100
        }
        document.rank = DBObject.numberToRank(rank)
        setNewDocument(document)
        setCurrentPasDoc(document._id)
        rt.setEditingPassageDocument(true)
    }

    // If a newly created document is open, it is always the one we want to be selected.
    let _index = newDocument ? sortedAllDocs.length - 1 : index
    _index = Math.min(_index, sortedAllDocs.length - 1)
    _index = Math.max(_index, 0)
    log('render passage documents', fmt(sortedAllDocs.map(doc => doc._id)), fmt({ index, _index, newDocument: newDocument, currentPasDoc }))

    return (
        <div>
            <Tabs selectedIndex={_index}
                onSelect={(_index) => { 
                    !rt.editingPassageDocument && setCurrentPasDoc(sortedAllDocs[_index]?._id)
                }}>
                <TabList>
                    {sortedAllDocs.map((document, i) => (
                        <Tab key={document._id + '-' + i}>
                            {documentTitle(document, i)}
                            {flags.passageDocScope && <ScopeButtonMenu
                                document={document}
                                enabled={canUserChangeScope(rt, document) && !rt.editingPassageDocument && newDocument === null}
                                changeDocumentScope={async (scopeLevel: DocumentScopeLevel) => {
                                    const origScope = document.scope || passage._id
                                    const iOrigScopeLevel = origScope === 'project' ? 0 : origScope.split('/').length
                                    const iNewScopeLevel = [
                                        DocumentScopeLevel.Project, DocumentScopeLevel.Portion, DocumentScopeLevel.Passage
                                    ].indexOf(scopeLevel)
                                    const isUpgrade = iNewScopeLevel <= iOrigScopeLevel

                                    // iTabTarget depends on whether the passage document scope is
                                    // upgrading or downgrading
                                    // in case of downgrading, since we are removing the document from 
                                    // the previous group, subtract 1 from the iUpgrade target
                                    let iTabTarget = 0
                                    if (scopeLevel === DocumentScopeLevel.Passage) {
                                        const iUpgrade = sortedAllProjectDocs.length + sortedAllPortionDocs.length
                                        iTabTarget = isUpgrade ? iUpgrade : Math.max(0, iUpgrade - 1)
                                    } else if (scopeLevel === DocumentScopeLevel.Portion) {
                                        const iUpgrade = sortedAllProjectDocs.length
                                        iTabTarget = isUpgrade ? iUpgrade : iUpgrade - 1
                                    } else {
                                        // target is Project
                                        iTabTarget = 0
                                    }

                                    const portionId = passage._id.split('/')[0]
                                    let scope
                                    if (scopeLevel === DocumentScopeLevel.Project) {
                                        scope = 'project'
                                    } else if (scopeLevel === DocumentScopeLevel.Portion) {
                                        scope = portionId
                                    } else if (scopeLevel === DocumentScopeLevel.Passage) {
                                        scope = passage._id
                                    } else {
                                        throw new Error(`Invalid scope level: ${scopeLevel}`)
                                    }
                                    await document.setScope(scope)
                                    await move(sortedAllDocs, i, iTabTarget)

                                    log(`PassageDocuments: Scope change completed for document ${document._id}`)
                                }}
                            />}
                        </Tab>
                    ))}
                    {rt.iAmInterpreter && (
                        <Tab key='add-document' disabled>
                            <AddButton
                                className='passage-document-add-button small-passage-document-button'
                                enabled={!rt.editingPassageDocument}
                                onClick={addDocument}
                                tooltip={/* translator: important */ t`Add passage document`}
                            />
                        </Tab>
                    )}
                </TabList>
                {sortedAllDocs.map((document, i) => (
                    <TabPanel key={document._id + '/' + i}>
                        <PassageDocumentEditor
                            document={document}
                            passage={passage}
                            pinDocument={async () => {
                                await passage.pinDocument(document._id)
                            }}
                            closeEditor={() => {
                                log('closeEditor')
                                if (newDocument) {
                                    // NOTE: setting two state variables in a row
                                    // can lead to unexpected state transitions
                                    // but this seems to work if we 
                                    // set the index based on the new document _id
                                    setNewDocument(null)
                                    setCurrentPasDoc(newDocument._id)
                                }
                            }}
                            rt={rt}
                            editingNewDocument={newDocument === document}
                        />
                    </TabPanel>
                ))}
                {rt.iAmInterpreter && (
                    <TabPanel key='add-document'>
                        <div className="passage-document__editing-area" />
                    </TabPanel>
                )}
            </Tabs>
        </div>
    )
})

enum DocumentScopeLevel {
    Passage = 'passage',
    Portion = 'portion',
    Project = 'project'
}

function documentTitle(document: PassageDocument, index: number) {
    /**
     * If a document was created with 1.5.2 or earlier there was no 'title' attribute.
     * Previously we extracted the title from the first line of the document.
     * This was confusing for users and unreliable when they edited the document to include
     * something complicated in the first line.
     *
     * If the document was created under this system we default the title to the first line
     * until the user edits the document (making the title something other than undefined)
     */
    const title = (document.title ?? document.getTitle()) || `#${index + 1}`
    return <span style={{ display: 'inline-masonry', verticalAlign: 'middle' }}>{title}</span>
}

function documentIcon(document: PassageDocument) {
    /**
     * Add icons for passage documents with scopes 'higher' than passage level.
     * This is used to indicate the scope of the document in the tab title.
     */
    // Project Level
    if (document.scope === 'project') {
        return <SquareSquare size={16} style={{ marginLeft: '5px' }} />
    }
    // Portion level
    if (document.scope?.split('/').length === 1) {
        return <Square size={16} style={{ marginLeft: '5px' }} />
    }
    // Passage level
    return <RectangleHorizontal size={16} style={{ marginLeft: '5px' }} />
}

const ScopeButton = ({ enabled, onClick, icon }: {
    enabled: boolean,
    onClick?: (e: React.MouseEvent) => void,
    icon: React.ReactNode,
}) => (
    <button className='wraparound-button' onClick={e => enabled && onClick && onClick(e)} title={enabled ? t`Set scope`: undefined}>
        {icon}
    </button>
)

type ScopeButtonMenuProps = {
    document: PassageDocument,
    enabled: boolean,
    changeDocumentScope: (scopeLevel: DocumentScopeLevel) => Promise<void>,
}

const ScopeButtonMenu: FC<ScopeButtonMenuProps> = observer(({
    document,
    enabled,
    changeDocumentScope,
}) => {
    const [showPinScopeDropdown, setShowPinScopeDropdown] = useState(false)
    const pinScopeDropdownContainerRef = useRef<HTMLDivElement>(null)

    useOnClickOutside(pinScopeDropdownContainerRef, () => {
        if (showPinScopeDropdown) {
            setShowPinScopeDropdown(false)
        }
    })

    return (
        <div className="passage-document-scope-dropdown-container" ref={pinScopeDropdownContainerRef}>
            <ScopeButton
                enabled={enabled}
                onClick={() => setShowPinScopeDropdown(!showPinScopeDropdown)}
                icon={documentIcon(document)}
            />
            <div className="passage-document-scope-dropdown" style={{ display: showPinScopeDropdown ? 'block' : 'none' }}>
                <div className="passage-document-scope-dropdown-item"
                    title={t`Passage`}
                    onClick={async () => {
                        await changeDocumentScope(DocumentScopeLevel.Passage)
                        setShowPinScopeDropdown(false)
                    }}>
                    <RectangleHorizontal size={16} className="passage-document-scope-dropdown-icon" />
                </div>
                <div className="passage-document-scope-dropdown-item"
                    title={t`Portion`}
                    onClick={async () => {
                        await changeDocumentScope(DocumentScopeLevel.Portion)
                        setShowPinScopeDropdown(false)
                    }}>
                    <Square size={16} className="passage-document-scope-dropdown-icon" />
                </div>
                <div className="passage-document-scope-dropdown-item"
                    title={t`Project`}
                    onClick={async () => {
                        await changeDocumentScope(DocumentScopeLevel.Project)
                        setShowPinScopeDropdown(false)
                    }}>
                    <SquareSquare size={16} className="passage-document-scope-dropdown-icon" />
                </div>
            </div>
        </div>
    )
})

type PassageDocumentHistorySelectorProps = {
    document: PassageDocument,
    passage: Passage,
    rt: Root,
    historyIndex: number,
    setHistoryIndex: (index: number) => void,
}

const PassageDocumentHistorySelector: FC<PassageDocumentHistorySelectorProps> = ({ document, passage, rt, historyIndex, setHistoryIndex }) => {
    let creations = [...document.creationHistory]
        .reverse()
        .map(creation => {
            let parts = creation.split('|')
            let pieces = parts[0].split('@')
            let pieces2 = rt.dateFormatter.format(convertFromCanonicalTimestamp(parts[1])).split(':')
            return pieces[0] + ' ' + pieces2.slice(0, -1).join(':')
        })

    return (
        <select className='document-history-selector'
            value={historyIndex}
            onChange={e => setHistoryIndex(parseInt(e.target.value)) }>
            {creations.map((creation, index) => (<option key={`${index}-${creation}`} value={index.toString()}> {creation} </option>) )}
        </select>
    )
}

type PassageDocumentEditorProps = {
    document: PassageDocument,
    passage: Passage,
    closeEditor: () => void,
    pinDocument: () => void,
    rt: Root,
    editingNewDocument: boolean,
}

// If this is a long document retrieve its text from S3

const PassageDocumentEditor: FC<PassageDocumentEditorProps> = observer(
    ({ document, passage, closeEditor, pinDocument, rt, editingNewDocument }
) => {
    let { project, username, iAmAdmin, iAmInterpreter, memberRole, setEditingPassageDocument } = rt

    const flags = useFlags()

    const [editing, _setEditing] = useState(editingNewDocument)
    function setEditing(editing: boolean) {
        _setEditing(editing)
        setEditingPassageDocument(editing)
    }

    let [savedText, setSavedText] = useState(editingNewDocument ? '' : '...')

    let title = document.title === undefined ? document.getTitle() : document.title
    log('PassageDocumentEditor render', fmt({ title, documentTitle: document.title, getTitle: document.getTitle() }))

    // 0 means most recent version of document, 1 next most recent, ...
    const [historyIndex, setHistoryIndex] = useState(0)

    const titleRef = useRef<HTMLInputElement|null>(null)
    const indexInDocuments = passage.documents.indexOf(document)

    const { zoom, zoomIn, zoomOut } = useZoom()

    // On initial load, get most recent text for document
    useEffect(() => {
        async function _loadText() {
            // We don't want to load an updated text when editing because the user
            // would lose their edits.
            if (editing) return

            log('PassageDocumentEditor _loadText')
            // getText deals with the fact that large documents are stored in S3 since
            // they are too big to store in DynamoDB.
            setSavedText(await document.getText(0, VideoCache))

            // When a new document edit arrives, make it the one in view
            // by resetting history location.
            // The user needs to realize that there is new document.
            setHistoryIndex(0)
        }

        _loadText()
    }, [document.text])

    function deleteDocument() {
        const beaconId = 'delete passage document button'
        const timestampOpened = new Date().toISOString()
        const creationHistory = stringifyCreationHistoryForBeacon(document.creationHistory)
        const s3Url = document.getS3Url()
        const getPayload = (timestamp: string, action: 'opened'|'confirmed') => ({
            timestamp, action, username, memberRole,
            _title: document.title, pasDocId: document._id, s3Url, textLength: document.text.length,
            creationDate: document.creationDate, creationHistory,
        })
        doOnceWhenBackOnline(`send beacon for delete document button OPENED: ${document._id}`,
            () => sendBeacon(beaconId, getPayload(timestampOpened, 'opened'))
        )
        confirmDelete({
            title: /* translator: important */ t`Delete document?`,
            message: /* translator: important */ t`Are you sure you want to delete this document?`,
            confirmLabel: /* translator: important */ t`Delete`,
            cancelLabel: /* translator: important */ t`Cancel`,
            onConfirm: async () => {
                const timestampConfirmed = new Date().toISOString()
                doOnceWhenBackOnline(`send beacon for delete document button CONFIRMED: ${document._id}`,
                    () => sendBeacon(
                        beaconId, getPayload(timestampConfirmed, 'confirmed')
                    )
                )
                await passage.removePassageDocument(document._id)
                closeEditor()
            },
            onCancel: () => {},
        })
    }

    let canModifyEditingStatus = iAmAdmin || document.creator === username
    let editButtonsEnabled = document.editable || canModifyEditingStatus

    async function _setHistoryIndex(index: number) {
        setHistoryIndex(index)
        let text = await document.getText(index, VideoCache)
        setSavedText(text)
    }

    return (
        <>
            {iAmInterpreter && !editing && (
                <div className='passage-document-toolbar'>
                    <div className='passage-document-toolbar-first-group'>
                        <PencilButton
                            enabled={editButtonsEnabled}
                            onClick={() => setEditing(true)}
                            className='passage-document-button passage-document-edit-button'
                            tooltip={/* translator: important */ t`Edit`}
                        />
                    </div>
                    <div className='passage-document-toolbar-middle-group'>
                        {/* <GoBackButton
                            enabled={!editing && historyIndex < document.textHistory.length-1}
                            onClick={() => _setHistoryIndex(historyIndex+1).catch(displayError)}
                            className='passage-document-button'
                            tooltip={t`View earlier document.`}
                        /> */}
                        <PassageDocumentHistorySelector {...{ document, passage, rt, historyIndex, setHistoryIndex: _setHistoryIndex }} />
                        {/* <GoForwardButton
                            enabled={!editing && historyIndex > 0}
                            onClick={() => _setHistoryIndex(historyIndex - 1).catch(displayError)}
                            className='passage-document-button'
                            tooltip={t`View later document`}
                        /> */}
                    </div>
                    <div className='passage-document-toolbar-last-group'>
                        <ZoomInDocumentButton enabled={true} onClick={zoomIn} />
                        <ZoomOutDocumentButton enabled={true} onClick={zoomOut} />
                        <TrashButton
                            onClick={deleteDocument}
                            enabled={editButtonsEnabled}
                            buttonClassName='button-right-align'
                            className='passage-document-button'
                            tooltip={/* translator: important */ t`Delete`}
                        />
                        {document.editable && canModifyEditingStatus && (
                            <UnlockButton
                                enabled={true}
                                onClick={() => document.setEditable(false)}
                                className='passage-document-button'
                                tooltip={t`Allow only admin or document creator to edit`}
                            />
                        )}
                        {!document.editable && canModifyEditingStatus && (
                            <LockButton
                                enabled={true}
                                onClick={() => document.setEditable(true)}
                                className='passage-document-button'
                                tooltip={t`Allow anyone to edit`}
                            />
                        )}
                        {!flags.passageDocScope && iAmInterpreter && indexInDocuments > 0 && <PinButton
                            enabled={true}
                            onClick={pinDocument}
                            className='passage-document-button'
                            tooltip={t`Move document to first tab`}
                        />}
                    </div>
                </div>
            )}
            { editing &&
                <div className="passage-document-title-input">
                    <input type="text"
                           ref={titleRef}
                           defaultValue={title ?? ''}
                           placeholder={t`Title...`} />
                    &nbsp;&nbsp;&nbsp;
                    <ZoomInDocumentButton enabled={true} onClick={zoomIn} />
                    <ZoomOutDocumentButton enabled={true} onClick={zoomOut} />
                </div>
            }
            <EditableRichText
                zoom={zoom}
                savedText={savedText}
                save={(_text?: string, shouldBeacon = false) => {
                    async function _save() {
                        log('PassageDocumentEditor save', fmt({ title: titleRef.current?.value, text: _text?.slice(0, 50) }))

                        let title = trimmedTitle(titleRef)
                        log('setTitle', fmt({ title }))

                        // in case of refresh, try to save the text BEFORE title
                        if (_text !== undefined) {
                            await document.setText(_text, rt.project.name, title, shouldBeacon)
                            setSavedText(_text)
                        }

                        if (title !== '') {
                            await document.setTitle(title)
                        }

                        if (editingNewDocument) {
                            // also save the rank of the document
                            await document.setRank(document.rankAsNumber)
                        }
                        
                        setHistoryIndex(0)
                        setEditing(false)
                        closeEditor()
                    }
                    _save().catch(displayError)
                }}
                cancel={() => {
                    log(`PassageDocumentEditor cancel, editingNewDocument=${editingNewDocument}`)
                    setEditing(false)
                    closeEditor()
                }}
                editorOpen={editing}
                setEditorOpen={setEditing}
                prefix={'passage-document'}
            />
        </>
    )
})

export default PassageDocuments

function trimmedTitle(titleRef: React.MutableRefObject<HTMLInputElement | null>) {
    let title = titleRef.current?.value ?? ''
    title = title.trim()
    return title
}
